/**
 * Реестр контуров (`/contours`), план v2 §8: шапка → KPI → фильтры
 * (массив · культура · геометрия · карантин) → таблица 7/12 | карта 5/12 →
 * страницы таблицы.
 *
 * Культура строки — из того же размещения, что красит карту
 * (`useContourGeo`: строка реестра, а если в ней пусто — культура фичи на
 * дату среза), поэтому строка и карта не расходятся. Массив — только фильтр
 * (`?massif=` в адресе), не переход на страницу массива (решение №85).
 * Карантин — тот же набор, что у страницы «Карантин» и слоя карты.
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FunnelX, LocateFixed } from 'lucide-react';
import { InfoPopover } from '../../components/InfoPopover';
import { useAnalysisContext } from '../../analysis/context';
import { PageHeader } from '../ui/PageHeader';
import { Card, Grid, Stack } from '../ui/Card';
import { KpiCard } from '../ui/KpiCard';
import { DataTable, type Column } from '../ui/DataTable';
import { EvidenceMark } from '../ui/EvidenceMark';
import { Badge } from '../ui/Badge';
import { ChoiceSelect, type Choice } from '../ui/AnalysisControls';
import { Icon } from '../ui/Icon';
import { MapWidget } from '../map/MapWidget';
import { useContourGeo } from '../map/useContourGeo';
import { DataGate } from './parts';
import { SkeletonKpi, SkeletonTable } from '../ui';
import { usePageTrail } from '../shell/ShellContext';
import { useTrackData } from '../data/useTrackData';
import { defineMessages } from '../../i18n/define';
import { useT, UNIT_LABELS } from '../../i18n/hooks';
import { FILES } from '../data/loader';
import { ha, num, placementUnknown } from '../lib/format';
import {
  CONTOUR_PAGE_SIZE,
  contourCropOf,
  contourCycleLabel,
  filterContours,
  NOT_PLACED,
  pageOf,
  type ContourFilters,
  type QuarantineFilter,
} from './contoursView';
import type { ContourMeta, Crop, Farm, Massif, QuarantineFile, QuarantineStatusValue } from '../data/types';
import './pages.css';
import './registry.css';
import './domain.css';
import './quarantineWave2.css';
import './contours.css';

const ALL = 'all';

/** «Қашқадарё массиви» → «Қашқадарё»: слово «массив» уже в заголовке колонки. */
const shortMassif = (name: string) => name.replace(/\s+массиви?$/i, '');

const M = defineMessages({
  trailDistrict: { id: 'contour.registry.trail.district', defaultMessage: 'Район', description: 'Уровень в цепочке навигации над заголовком (хлебные крошки): район' },
  trailDistrictName: { id: 'contour.registry.trail.districtName', defaultMessage: 'Бука', description: 'Название района (Бука) в цепочке навигации; имя собственное' },
  trailSection: { id: 'contour.registry.trail.section', defaultMessage: 'Раздел', description: 'Уровень в цепочке навигации: раздел приложения' },
  trailName: { id: 'contour.registry.trail.name', defaultMessage: 'Контуры', description: 'Название раздела в цепочке навигации: реестр контуров' },
  title: { id: 'contour.registry.title', defaultMessage: 'Контуры', description: 'Заголовок страницы: реестр контуров района (кадастровых участков земли)' },
  purpose: { id: 'contour.registry.purpose', defaultMessage: 'Все контуры среза, включая записи без геометрии.', description: 'Описание под заголовком реестра контуров' },
  howToRead: { id: 'contour.registry.help.label', defaultMessage: 'Как читать реестр', description: 'Подпись кнопки-подсказки (aria-label) и заголовок подсказки у реестра контуров' },
  help1: { id: 'contour.registry.help.p1', defaultMessage: 'Записи без геометрии живут строкой, а не полигоном: на карте их нет («нет на карте»). «Кандидат» — геометрия не подтверждена; кнопка-прицел показывает контур на карте. Фильтры сужают таблицу и карту, но не числа сверху — те всегда про весь реестр.', description: 'Подсказка к реестру контуров, абзац 1: записи без геометрии, кандидаты, фильтры' },
  help2: { id: 'contour.registry.help.p2', defaultMessage: 'Культура — то, что стоит на поле на дату среза, из того же размещения, что красит карту (демо-слой базы района, не подтверждённый посев). Пусто — размещение не названо, а не «пар».', description: 'Подсказка к реестру контуров, абзац 2: откуда берётся культура; «пар» — земля под парами' },
  help3: { id: 'contour.registry.help.p3', defaultMessage: 'Карантин — статус по ленте событий, как на странице «Карантин». Прочерк — событий не было: зелёный по умолчанию, а не «проверено».', description: 'Подсказка к реестру контуров, абзац 3: как читать колонку карантина' },
  help4: { id: 'contour.registry.help.p4', defaultMessage: 'Реестр — текущее состояние; землепользователь — основной по базе района, «и ещё N» — земля делится.', description: 'Подсказка к реестру контуров, абзац 4: землепользователь контура' },

  skelTotal: { id: 'contour.registry.skeleton.total', defaultMessage: 'всего контуров', description: 'Что загружается (подпись скелетона KPI для чтения с экрана), после слова «Загружаю»' },
  skelAccepted: { id: 'contour.registry.skeleton.accepted', defaultMessage: 'с принятой геометрией', description: 'Что загружается (подпись скелетона KPI для чтения с экрана)' },
  skelPlaced: { id: 'contour.registry.skeleton.placed', defaultMessage: 'под культурами на дату', description: 'Что загружается (подпись скелетона KPI для чтения с экрана)' },
  skelHot: { id: 'contour.registry.skeleton.hot', defaultMessage: 'красные и жёлтые', description: 'Что загружается (подпись скелетона KPI для чтения с экрана)' },
  skelTable: { id: 'contour.registry.skeleton.table', defaultMessage: 'реестр контуров', description: 'Что загружается (подпись скелетона таблицы для чтения с экрана)' },
  kpiTotal: { id: 'contour.registry.kpi.total', defaultMessage: 'Всего контуров', description: 'Подпись KPI: сколько контуров в реестре среза' },
  kpiTotalNote: { id: 'contour.registry.kpi.totalNote', defaultMessage: 'в реестре среза', description: 'Мелкая подпись под KPI «Всего контуров»' },
  kpiAccepted: { id: 'contour.registry.kpi.accepted', defaultMessage: 'С принятой геометрией', description: 'Подпись KPI: сколько контуров с подтверждённой геометрией (полигон на карте)' },
  kpiOfContours: { id: 'contour.registry.kpi.ofContours', defaultMessage: 'из {count, plural, one {# контура} few {# контуров} many {# контуров} other {# контуров}}', description: 'Мелкая подпись под KPI: «из N контуров» в реестре; {count} — всего контуров' },
  kpiNotRead: { id: 'contour.registry.kpi.notRead', defaultMessage: 'реестр не прочитан', description: 'Мелкая подпись под KPI, когда реестр контуров не загрузился' },
  kpiPlaced: { id: 'contour.registry.kpi.placed', defaultMessage: 'Под культурами на дату', description: 'Подпись KPI: сколько контуров занято культурой на дату среза' },
  kpiPlacedMissing: { id: 'contour.registry.kpi.placedMissing', defaultMessage: 'размещение не пришло', description: 'Мелкая подпись под KPI «Под культурами», когда источник размещения не передал' },
  kpiHot: { id: 'contour.registry.kpi.hot', defaultMessage: 'Красные и жёлтые', description: 'Подпись KPI: контуры с красным или жёлтым карантинным статусом' },
  kpiHotUnit: { id: 'contour.registry.kpi.hotUnit', defaultMessage: '{count, plural, one {контур} few {контура} many {контуров} other {контура}}', description: 'Единица рядом с числом KPI «Красные и жёлтые»: «5 контуров»; без числа' },
  kpiHotNote: { id: 'contour.registry.kpi.hotNote', defaultMessage: 'карантин — то же число, что на «Обзоре района»', description: 'Мелкая подпись под KPI «Красные и жёлтые»' },
  kpiHotNotRead: { id: 'contour.registry.kpi.hotNotRead', defaultMessage: 'статусы карантина не прочитаны', description: 'Мелкая подпись под KPI «Красные и жёлтые», когда статусы карантина не загрузились' },

  geomAll: { id: 'contour.registry.filter.geometry.all', defaultMessage: 'Все статусы', description: 'Вариант фильтра «Геометрия»: любой статус геометрии' },
  geomAccepted: { id: 'contour.registry.filter.geometry.accepted', defaultMessage: 'Принята', description: 'Вариант фильтра «Геометрия»: геометрия принята' },
  geomCandidate: { id: 'contour.registry.filter.geometry.candidate', defaultMessage: 'Кандидат', description: 'Вариант фильтра «Геометрия»: геометрия-кандидат, не подтверждена' },
  geomNone: { id: 'contour.registry.filter.geometry.none', defaultMessage: 'Нет геометрии', description: 'Вариант фильтра «Геометрия»: геометрии нет' },
  qAll: { id: 'contour.registry.filter.quarantine.all', defaultMessage: 'Все контуры', description: 'Вариант фильтра «Карантин»: без ограничения' },
  qHot: { id: 'contour.registry.filter.quarantine.hot', defaultMessage: 'Очаги: красные и жёлтые', description: 'Вариант фильтра «Карантин»: красные и жёлтые контуры' },
  qRed: { id: 'contour.registry.filter.quarantine.red', defaultMessage: 'Только красные', description: 'Вариант фильтра «Карантин»: только красные' },
  qWatch: { id: 'contour.registry.filter.quarantine.watch', defaultMessage: 'Только наблюдение', description: 'Вариант фильтра «Карантин»: только жёлтые (под наблюдением)' },
  massifAll: { id: 'contour.registry.filter.massif.all', defaultMessage: 'Все массивы', description: 'Вариант фильтра «Массив»: все массивы' },
  cropAll: { id: 'contour.registry.filter.crop.all', defaultMessage: 'Все культуры', description: 'Вариант фильтра «Культура»: все культуры' },
  cropUnplaced: { id: 'contour.registry.filter.crop.unplaced', defaultMessage: 'Размещение неизвестно', description: 'Вариант фильтра «Культура»: размещение контура не названо' },
  filterMassif: { id: 'contour.registry.filter.massif', defaultMessage: 'Массив', description: 'Подпись фильтра: массив (группа хозяйств и земель)' },
  filterCrop: { id: 'contour.registry.filter.crop', defaultMessage: 'Культура', description: 'Подпись фильтра: культура' },
  filterGeometry: { id: 'contour.registry.filter.geometry', defaultMessage: 'Геометрия', description: 'Подпись фильтра: статус геометрии контура' },
  filterQuarantine: { id: 'contour.registry.filter.quarantine', defaultMessage: 'Карантин', description: 'Подпись фильтра: карантинный статус' },
  selectedOf: { id: 'contour.registry.filter.selectedOf', defaultMessage: 'Отобрано {shown} из {total}', description: 'Строка над кнопкой сброса фильтров; {shown} — сколько контуров отобрано, {total} — всего; числа отформатированы' },
  selected: { id: 'contour.registry.filter.selected', defaultMessage: 'Отобрано {count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}', description: 'Строка над кнопкой сброса фильтров, когда общее число неизвестно; {count} — отобрано контуров' },
  reset: { id: 'contour.registry.filter.reset', defaultMessage: 'Сбросить фильтры', description: 'Кнопка сброса всех фильтров реестра контуров' },

  qtagRed: { id: 'contour.registry.quarantine.red', defaultMessage: 'красный', description: 'Бейдж карантина в таблице реестра: красный статус; коротко' },
  qtagWatch: { id: 'contour.registry.quarantine.watch', defaultMessage: 'наблюдение', description: 'Бейдж карантина в таблице реестра: жёлтый статус, наблюдение; коротко' },
  qtagGreen: { id: 'contour.registry.quarantine.green', defaultMessage: 'устранён', description: 'Бейдж карантина в таблице реестра: очаг устранён; коротко' },

  colNo: { id: 'contour.registry.col.no', defaultMessage: 'Номер', description: 'Колонка таблицы реестра: номер контура; узкая колонка' },
  contourNo: { id: 'contour.registry.col.contourNo', defaultMessage: '№ {no}', description: 'Номер контура в ячейке таблицы; {no} — кадастровый номер' },
  notOnMap: { id: 'contour.registry.badge.notOnMap', defaultMessage: 'нет на карте', description: 'Бейдж у номера контура: геометрии нет, на карте не показан; коротко' },
  candidate: { id: 'contour.registry.badge.candidate', defaultMessage: 'Кандидат', description: 'Метка у номера контура: геометрия не подтверждена; коротко' },
  locateAria: { id: 'contour.registry.locate.aria', defaultMessage: 'Показать контур № {no} на карте', description: 'aria-label кнопки-прицела в строке реестра; {no} — номер контура' },
  locateTitle: { id: 'contour.registry.locate.title', defaultMessage: 'Показать на карте', description: 'Подсказка кнопки-прицела в строке реестра' },
  colFarm: { id: 'contour.registry.col.farm', defaultMessage: 'Хозяйство · массив', description: 'Колонка таблицы реестра: хозяйство и под ним массив' },
  noLandUser: { id: 'contour.registry.landUser.none', defaultMessage: 'землепользователь не указан', description: 'Ячейка колонки «Хозяйство»: у контура не указан землепользователь' },
  andMore: { id: 'contour.registry.landUser.andMore', defaultMessage: 'и ещё {count}', description: 'Мелкая пометка после названия хозяйства: у контура есть ещё землепользователи; {count} — сколько ещё' },
  massifPickTitle: { id: 'contour.registry.massif.pickTitle', defaultMessage: 'Отобрать контуры этого массива', description: 'Подсказка кнопки с названием массива в строке: включить фильтр по массиву' },
  colArea: { id: 'contour.registry.col.area', defaultMessage: 'Площадь', description: 'Колонка таблицы реестра: площадь контура, га земли; числовая колонка' },
  colCrop: { id: 'contour.registry.col.crop', defaultMessage: 'Культура', description: 'Колонка таблицы реестра: культура на дату среза' },
  colCycle: { id: 'contour.registry.col.cycle', defaultMessage: 'Агроцикл / культура', description: 'Колонка таблицы реестра: агроцикл контура (культура × сезон)' },
  colQuarantine: { id: 'contour.registry.col.quarantine', defaultMessage: 'Карантин', description: 'Колонка таблицы реестра: карантинный статус; узкая колонка' },

  gateWhat: { id: 'contour.registry.gate.what', defaultMessage: 'реестр контуров не получен', description: 'Что не пришло (подпись пустого состояния данных): файл реестра контуров не загружен' },
  denominator: { id: 'contour.registry.table.denominator', defaultMessage: '{count, plural, one {контур} few {контура} many {контуров} other {контура}}', description: 'Слово-знаменатель под таблицей реестра: «25 из 10 812 контуров»; без числа' },
  emptyTitle: { id: 'contour.registry.table.empty.title', defaultMessage: 'Контуров не нашлось', description: 'Заголовок пустой таблицы реестра контуров' },
  emptyFiltered: { id: 'contour.registry.table.empty.filtered', defaultMessage: 'по выбранным фильтрам совпадений нет — измените фильтры', description: 'Пояснение к пустой таблице реестра при включённых фильтрах' },
  emptyAll: { id: 'contour.registry.table.empty.all', defaultMessage: 'в срезе не оказалось ни одной записи', description: 'Пояснение к пустой таблице реестра без фильтров' },
  pagerAria: { id: 'contour.registry.pager.aria', defaultMessage: 'Страницы реестра', description: 'aria-label блока переключения страниц реестра контуров' },
  pagerBack: { id: 'contour.registry.pager.back', defaultMessage: 'Назад', description: 'Кнопка: предыдущая страница реестра; коротко' },
  pagerNext: { id: 'contour.registry.pager.next', defaultMessage: 'Дальше', description: 'Кнопка: следующая страница реестра; коротко' },
  pagerInfo: { id: 'contour.registry.pager.info', defaultMessage: '{from}–{to} из {total} · страница {page} из {pages}', description: 'Подпись между кнопками страниц; {from}–{to} — номера строк, {total} — всего строк, {page} и {pages} — текущая и последняя страница; числа отформатированы' },
});

const geometryOptions = (t: ReturnType<typeof useT>): Choice[] => [
  { id: ALL, name: t(M.geomAll) },
  { id: 'accepted', name: t(M.geomAccepted) },
  { id: 'candidate', name: t(M.geomCandidate) },
  { id: 'none', name: t(M.geomNone) },
];

const quarantineOptions = (t: ReturnType<typeof useT>): Choice[] => [
  { id: ALL, name: t(M.qAll) },
  { id: 'hot', name: t(M.qHot) },
  { id: 'red', name: t(M.qRed) },
  { id: 'watch', name: t(M.qWatch) },
];

function QuarantineCell({ value }: { value: QuarantineStatusValue | undefined }) {
  const t = useT();
  if (value === 'red') return <span className="qtag qtag--red">{t(M.qtagRed)}</span>;
  if (value === 'watch') return <span className="qtag qtag--watch">{t(M.qtagWatch)}</span>;
  if (value === 'green') return <span className="qtag qtag--green">{t(M.qtagGreen)}</span>;
  return <span className="muted">—</span>;
}

export function Contours() {
  const t = useT();
  usePageTrail([
    { level: t(M.trailDistrict), name: t(M.trailDistrictName), to: '/' },
    { level: t(M.trailSection), name: t(M.trailName) },
  ]);

  const { linkSearch } = useAnalysisContext();
  const contours = useTrackData<ContourMeta[]>(FILES.contours);
  const massifs = useTrackData<Massif[]>(FILES.massifs);
  const farms = useTrackData<Farm[]>(FILES.farms);
  const crops = useTrackData<Crop[]>(FILES.crops);
  const quarantine = useTrackData<QuarantineFile>(FILES.quarantine);
  const geo = useContourGeo();

  // Массив — фильтр в адресе (`?massif=`): ссылки «массив» с других экранов
  // приводят сюда, а не на страницу массива.
  const [search, setSearch] = useSearchParams();
  const massifFilter = search.get('massif') ?? '';
  const setMassifFilter = (value: string) =>
    setSearch(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('massif', value);
        else next.delete('massif');
        return next;
      },
      { replace: true },
    );

  const [cropFilter, setCropFilter] = useState('');
  const [geomFilter, setGeomFilter] = useState('');
  const [quarantineFilter, setQuarantineFilter] = useState<QuarantineFilter>('');
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const massifNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of massifs.data ?? []) map.set(m.id, m.name);
    return map;
  }, [massifs.data]);

  const farmNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of farms.data ?? []) map.set(f.id, f.name);
    return map;
  }, [farms.data]);

  const cropNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of crops.data ?? []) map.set(c.id, c.name);
    return map;
  }, [crops.data]);

  /** Культура контура — та же, что красит карту. */
  const cropOf = useMemo(() => contourCropOf(geo.geo?.features ?? []), [geo.geo]);

  /** Контуры с полигоном на карте; пока геометрия читается — по статусу строки. */
  const mappedIds = useMemo(
    () => (geo.geo ? new Set(geo.geo.features.map((f) => String(f.properties?.id ?? ''))) : null),
    [geo.geo],
  );
  const onMap = (row: ContourMeta) => (mappedIds ? mappedIds.has(row.id) : row.geometry !== 'none');

  const quarantineById = useMemo(() => {
    const map: Record<string, QuarantineStatusValue> = {};
    for (const row of quarantine.data?.statuses ?? []) map[row.contour_id] = row.status;
    return map;
  }, [quarantine.data]);

  const cropName = (id: string | null) => (id ? cropNameById.get(id) ?? id : placementUnknown());

  const stats = useMemo(() => {
    const rows = contours.data ?? [];
    if (rows.length === 0) return null;
    return {
      total: rows.length,
      accepted: rows.filter((c) => c.geometry === 'accepted').length,
      placed: rows.filter((c) => cropOf(c) !== null).length,
      hot: Object.values(quarantineById).filter((s) => s !== 'green').length,
    };
  }, [contours.data, cropOf, quarantineById]);

  // У TrackData «нет файла» — `missing`, а не ошибка запроса, поэтому смотрим на state прямо.
  const contoursLoading = contours.state === 'loading';

  const filtersActive = massifFilter !== '' || cropFilter !== '' || geomFilter !== '' || quarantineFilter !== '';

  const filtered = useMemo(() => {
    const filters: ContourFilters = {
      massif: massifFilter,
      crop: cropFilter,
      geometry: geomFilter,
      quarantine: quarantineFilter,
    };
    return filterContours(contours.data ?? [], filters, cropOf, quarantineById);
  }, [contours.data, massifFilter, cropFilter, geomFilter, quarantineFilter, cropOf, quarantineById]);
  const paged = pageOf(filtered, page);
  const filterIds = useMemo(() => (filtersActive ? filtered.map((c) => c.id) : undefined), [filtersActive, filtered]);

  const resetPage = () => setPage(0);

  // Колонка «Агроцикл» — только когда источник называет цикл контура (канон);
  // у фикстур её нет, и пустой столбец прочерков вводил бы в заблуждение.
  const hasCycles = (contours.data ?? []).some((row) => row.cycle !== undefined);

  const columns: Column<ContourMeta>[] = [
    {
      key: 'no',
      header: t(M.colNo),
      render: (row) => (
        <span className="contours-no">
          <Link to={`/contours/${row.id}${linkSearch()}`}>{t(M.contourNo, { no: row.no })}</Link>
          {!onMap(row) ? <Badge tone="outline">{t(M.notOnMap)}</Badge> : null}
          {row.geometry === 'candidate' ? <EvidenceMark kind="candidate" label={t(M.candidate)} /> : null}
          {onMap(row) ? (
            <button
              type="button"
              className="contours-locate focus-ring"
              aria-pressed={selectedId === row.id}
              aria-label={t(M.locateAria, { no: row.no })}
              title={t(M.locateTitle)}
              onClick={() => setSelectedId(selectedId === row.id ? null : row.id)}
            >
              <Icon icon={LocateFixed} size={16} />
            </button>
          ) : null}
        </span>
      ),
    },
    {
      key: 'farm',
      header: t(M.colFarm),
      /**
       * Землепользователь у контура бывает не один. Канон называет основного
       * и отдельно — сколько их всего; показать одно имя молча значило бы
       * приписать общую землю одному хозяйству.
       */
      render: (row) => (
        <span className="contours-owner">
          {row.farm_id === '' ? (
            <span className="muted">{t(M.noLandUser)}</span>
          ) : (
            <span>
              <Link to={`/farms/${row.farm_id}${linkSearch()}`}>{farmNameById.get(row.farm_id) ?? row.farm_id}</Link>
              {(row.land_users_count ?? 1) > 1 ? (
                <span className="t-caption"> · {t(M.andMore, { count: num((row.land_users_count ?? 1) - 1) })}</span>
              ) : null}
            </span>
          )}
          {/* Массив — подпись места и фильтр этой таблицы и карты, а не переход. */}
          <button
            type="button"
            className="contours-massif focus-ring"
            title={t(M.massifPickTitle)}
            onClick={() => {
              setMassifFilter(row.massif_id);
              resetPage();
            }}
          >
            {shortMassif(massifNameById.get(row.massif_id) ?? row.massif_id)}
          </button>
        </span>
      ),
    },
    { key: 'area', header: t(M.colArea), unit: t(UNIT_LABELS.ha), numeric: true, render: (row) => (row.area_stated === false ? '—' : ha(row.area_ha)) },
    { key: 'crop', header: t(M.colCrop), render: (row) => cropName(cropOf(row)) },
    ...(hasCycles
      ? [
          {
            key: 'cycle',
            header: t(M.colCycle),
            render: (row: ContourMeta) => {
              const label = contourCycleLabel(row);
              if (!label) return <span className="muted">—</span>;
              return label.key ? (
                <Link to={`/cycles/${label.key}${linkSearch()}`}>{label.text}</Link>
              ) : (
                <span className="muted">{label.text}</span>
              );
            },
          } as Column<ContourMeta>,
        ]
      : []),
    { key: 'quarantine', header: t(M.colQuarantine), render: (row) => <QuarantineCell value={quarantineById[row.id]} /> },
  ];

  const massifOptions: Choice[] = [
    { id: ALL, name: t(M.massifAll) },
    ...[...(massifs.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'ru')).map((m) => ({ id: m.id, name: m.name })),
  ];
  const cropOptions: Choice[] = [
    { id: ALL, name: t(M.cropAll) },
    ...(crops.data ?? []).map((c) => ({ id: c.id, name: c.name })),
    { id: NOT_PLACED, name: t(M.cropUnplaced) },
  ];

  const focusIds = selectedId ? [selectedId] : filterIds && filterIds.length <= 2000 ? filterIds : undefined;
  const pages = Math.max(1, Math.ceil(filtered.length / CONTOUR_PAGE_SIZE));

  return (
    <Stack>
      <PageHeader
        level="district"
        title={t(M.title)}
        purpose={t(M.purpose)}
        actions={
          <InfoPopover icon="info" label={t(M.howToRead)} heading={t(M.howToRead)}>
            <p>{t(M.help1)}</p>
            <p>{t(M.help2)}</p>
            <p>{t(M.help3)}</p>
            <p>{t(M.help4)}</p>
          </InfoPopover>
        }
      />

      <div data-guide="kpi">
      <Grid cols={4}>
        {contoursLoading ? <SkeletonKpi label={t(M.skelTotal)} /> : <KpiCard label={t(M.kpiTotal)} value={stats ? num(stats.total) : '—'} note={t(M.kpiTotalNote)} />}
        {contoursLoading ? <SkeletonKpi label={t(M.skelAccepted)} /> : <KpiCard
          label={t(M.kpiAccepted)}
          value={stats ? num(stats.accepted) : '—'}
          note={stats ? t(M.kpiOfContours, { count: stats.total }) : t(M.kpiNotRead)}
        />}
        {contoursLoading ? <SkeletonKpi label={t(M.skelPlaced)} /> : <KpiCard
          label={t(M.kpiPlaced)}
          // Ноль здесь означал бы «ни один контур не размещён» — у источника без
          // размещения это неправда, поэтому пусто называется отсутствием данных.
          value={stats && stats.placed > 0 ? num(stats.placed) : '—'}
          note={stats ? (stats.placed > 0 ? t(M.kpiOfContours, { count: stats.total }) : t(M.kpiPlacedMissing)) : t(M.kpiNotRead)}
        />}
        {contoursLoading || quarantine.state === 'loading' ? <SkeletonKpi label={t(M.skelHot)} /> : <KpiCard
          label={t(M.kpiHot)}
          value={quarantine.data ? num(stats?.hot ?? 0) : '—'}
          unit={quarantine.data ? t(M.kpiHotUnit, { count: stats?.hot ?? 0 }) : undefined}
          note={quarantine.data ? t(M.kpiHotNote) : t(M.kpiHotNotRead)}
        />}
      </Grid>
      </div>

      <Card size="lg">
        <div className="filters contours-filters" data-guide="filters">
          <ChoiceSelect
            label={t(M.filterMassif)}
            value={massifFilter || ALL}
            options={massifOptions}
            onChange={(v) => {
              setMassifFilter(v === ALL ? '' : v);
              resetPage();
            }}
          />
          <ChoiceSelect
            label={t(M.filterCrop)}
            value={cropFilter || ALL}
            options={cropOptions}
            onChange={(v) => {
              setCropFilter(v === ALL ? '' : v);
              resetPage();
            }}
          />
          <ChoiceSelect
            label={t(M.filterGeometry)}
            value={geomFilter || ALL}
            options={geometryOptions(t)}
            onChange={(v) => {
              setGeomFilter(v === ALL ? '' : v);
              resetPage();
            }}
          />
          <ChoiceSelect
            label={t(M.filterQuarantine)}
            value={quarantineFilter || ALL}
            options={quarantineOptions(t)}
            onChange={(v) => {
              setQuarantineFilter(v === ALL ? '' : (v as QuarantineFilter));
              resetPage();
            }}
          />
          {filtersActive ? (
            <div className="filterfield filterfield--reset">
              <span className="filterfield__label">
                {stats ? t(M.selectedOf, { shown: num(filtered.length), total: num(stats.total) }) : t(M.selected, { count: filtered.length })}
              </span>
              <button
                type="button"
                className="btn btn--secondary btn--sm focus-ring"
                onClick={() => {
                  setMassifFilter('');
                  setCropFilter('');
                  setGeomFilter('');
                  setQuarantineFilter('');
                  resetPage();
                }}
              >
                <Icon icon={FunnelX} size={16} />
                {t(M.reset)}
              </button>
            </div>
          ) : null}
        </div>

        <div className="contours-split">
          <div className="contours-split__table" data-guide="table">
            <DataGate
              res={contours}
              what={t(M.gateWhat)}
              skeleton={<SkeletonTable label={t(M.skelTable)} rows={CONTOUR_PAGE_SIZE} cols={5} />}
            >
              {() => (
                <>
                  <DataTable
                    columns={columns}
                    rows={paged}
                    getKey={(row) => row.id}
                    limit={CONTOUR_PAGE_SIZE}
                    denominator={M.denominator}
                    empty={{
                      title: t(M.emptyTitle),
                      reason: filtersActive ? t(M.emptyFiltered) : t(M.emptyAll),
                    }}
                  />
                  {filtered.length > CONTOUR_PAGE_SIZE ? (
                    <nav className="contours-pager" aria-label={t(M.pagerAria)}>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm focus-ring"
                        disabled={page === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                      >
                        <Icon icon={ChevronLeft} size={16} />
                        {t(M.pagerBack)}
                      </button>
                      <span className="t-caption">
                        {t(M.pagerInfo, {
                          from: num(page * CONTOUR_PAGE_SIZE + 1),
                          to: num(Math.min(filtered.length, (page + 1) * CONTOUR_PAGE_SIZE)),
                          total: num(filtered.length),
                          page: num(page + 1),
                          pages: num(pages),
                        })}
                      </span>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm focus-ring"
                        disabled={page >= pages - 1}
                        onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
                      >
                        {t(M.pagerNext)}
                        <Icon icon={ChevronRight} size={16} />
                      </button>
                    </nav>
                  ) : null}
                </>
              )}
            </DataGate>
          </div>
          <div className="contours-split__map" data-guide="map">
            <MapWidget
              colorBy={quarantineFilter ? 'quarantine' : 'crop'}
              legend="collapsed"
              mode="frame"
              height={560}
              crops={crops.data ?? undefined}
              quarantineById={quarantineById}
              filterIds={filterIds}
              focusIds={focusIds}
              selectedId={selectedId}
              onSelect={(c) => setSelectedId(c?.id ?? null)}
              resolveNames={{
                farm: (id) => farmNameById.get(id) ?? null,
                massif: (id) => massifNameById.get(id) ?? null,
              }}
            />
          </div>
        </div>
      </Card>
    </Stack>
  );
}
